import { APP_PART_TYPES } from "@lhd/features/Apps/AppForm/constants";
import { AppPartType } from "@lhd/features/Apps/AppForm/types";
import FilterPanel from "@lhd/features/Apps/AppList/ManagementAppSheet/ManagementAppSheetList/AppSheetComponents/FilterPanelWrapper/FilterPanel";
import { IPropsFilterPanel } from "@lhd/features/Apps/AppList/ManagementAppSheet/ManagementAppSheetList/AppSheetComponents/FilterPanelWrapper/interface";
import { getDisplayText } from "@lhd/features/Apps/AppList/ManagementAppSheet/ManagementAppSheetList/utility";
import { useFetchFilterOptionsV2, getUpstreamActiveFilters } from "@lhd/hooks";
import {
  APP_TYPES,
  SELECTIONS_APP_PARTS,
  STATUS_CONTACT_OPTIONS,
} from "../../../../constants";
import { forwardRef, useEffect, useMemo } from "react";
import {
  getTextSection,
  getValueFormatByAppPart,
  flattenAppParts,
} from "@lhd/components/CloneApp/untils";
import { AppSelectionList } from "@lhd/types";
import { parseJson } from "@lhd/utility";
import dayjs from "dayjs";
interface IProps extends IPropsFilterPanel {
  appId: string;
  appPart?: AppPartType;
  appParts?: AppPartType[];
  appType: string;
  selectionDataList: AppSelectionList;
  companyId?: number | string;
  workerId?: number;
  additionalFilter?: Record<string, any>;
}

const FilterPanelWrapper = (props: IProps, ref: React.Ref<HTMLDivElement>) => {
  const {
    columnId,
    appId,
    appParts,
    appType,
    filter,
    selectionDataList,
    additionalFilter,
  } = props;

  const flatAppParts = useMemo(() => {
    return appParts ? flattenAppParts(appParts) : [];
  }, [appParts]);

  const appPart = useMemo(() => {
    return (
      flatAppParts.find((item) => String(item.id) === String(columnId)) ||
      props.appPart
    );
  }, [flatAppParts, columnId, props.appPart]);

  const isStatusContract =
    appPart?.app_part_type === APP_PART_TYPES.STATUS_CONTRACT;

  const getValueFilter = (
    appPart: AppPartType,
    value: string,
    label: string,
  ) => {
    if (
      appPart.app_part_type === APP_PART_TYPES.DATE_AND_TIME ||
      appPart.app_part_type === APP_PART_TYPES.DATE ||
      appPart.app_part_type === APP_PART_TYPES.TIME ||
      appPart.app_part_type === APP_PART_TYPES.SIGNED_DATE
    ) {
      return label;
    }
    return value;
  };

  const activeFilters = useMemo(() => {
    const result = {} as Record<string, (number | string)[]>;
    Object.keys(filter).forEach((key) => {
      const keyAs = key as keyof typeof filter;
      const valueFilter = filter[keyAs];
      if (valueFilter?.filter) {
        let originalValues: any[] = [];
        if (Array.isArray(valueFilter.filter)) {
          originalValues = valueFilter.filter;
        } else if (typeof valueFilter.filter === "object") {
          originalValues = Object.keys(valueFilter.filter).filter(
            (k) => valueFilter.filter[k] === true,
          );
        } else {
          originalValues = [valueFilter.filter];
        }

        if (valueFilter.filter_special_empty) {
          originalValues.push("__NULL__");
        }
        const part = flatAppParts.find(
          (item) =>
            String(item.id) === String(key) ||
            String(item.field_code) === String(key),
        );
        if (
          part &&
          (part.app_part_type === APP_PART_TYPES.DATE_AND_TIME ||
            part.app_part_type === APP_PART_TYPES.DATE ||
            part.app_part_type === APP_PART_TYPES.TIME ||
            part.app_part_type === APP_PART_TYPES.SIGNED_DATE)
        ) {
          result[key] = originalValues.map((v) => {
            const valStr = String(v);
            return valStr
              .replace(/^(\d{4})年(\d{2})月(\d{2})日$/, "$1-$2-$3")
              .replace(/^(\d{4})年(\d{2})月$/, "$1-$2")
              .replace(/^(\d{4})年$/, "$1");
          });
        } else {
          result[key] = originalValues;
        }
      }
    });
    return result;
  }, [filter, flatAppParts]);

  const hasActiveTimeFilter = useMemo(() => {
    const activeFilterKeys = Object.keys(activeFilters).filter(
      (key) => activeFilters[key] && activeFilters[key].length > 0,
    );
    if (activeFilterKeys.length === 0) return false;

    const isTimeByPart = activeFilterKeys.some((key) => {
      const part = flatAppParts.find(
        (item) =>
          String(item.id) === String(key) ||
          String(item.field_code) === String(key),
      );
      if (!part) return false;
      return (
        part.app_part_type === APP_PART_TYPES.DATE_AND_TIME ||
        part.app_part_type === APP_PART_TYPES.DATE ||
        part.app_part_type === APP_PART_TYPES.TIME ||
        part.app_part_type === APP_PART_TYPES.SIGNED_DATE
      );
    });

    if (isTimeByPart) return true;

    // Fallback: Check values
    const dateRegex = /^\d{4}[-/]\d{2}[-/]\d{2}/;
    const monthRegex = /^\d{4}[-/]\d{2}/;
    const isTimeByValue = activeFilterKeys.some((key) => {
      const values = activeFilters[key] || [];
      return values.some((val) => {
        const valStr = String(val);
        return (
          valStr.includes("年") ||
          valStr.includes("月") ||
          valStr.includes("日") ||
          dateRegex.test(valStr) ||
          monthRegex.test(valStr)
        );
      });
    });

    return isTimeByValue;
  }, [activeFilters, flatAppParts]);

  const upstreamActiveFilters = useMemo(() => {
    const raw = getUpstreamActiveFilters(
      activeFilters as Record<string, string[]>,
      flatAppParts,
      (col) => String(col.id),
      String(columnId),
      props.filterInfo?.filterOrder,
    );

    return raw;
  }, [
    activeFilters,
    flatAppParts,
    columnId,
    props.filterInfo?.filterOrder,
    appType,
  ]);

  const { data, isLoading, isFetching, refetch } = useFetchFilterOptionsV2({
    appId,
    data: hasActiveTimeFilter
      ? {
          field_ids: [String(columnId)],
          active_filters: upstreamActiveFilters,
          ...additionalFilter,
        }
      : {
          app_part_ids: [String(columnId)],
          active_filters: upstreamActiveFilters,
          ...additionalFilter,
        },
    appType,
    enabled: !isStatusContract && !!columnId && !!props.open,
    companyId: props.companyId,
    workerId: props.workerId,
  });

  const needsSelectionData = SELECTIONS_APP_PARTS.includes(
    appPart?.app_part_type as string,
  );

  const isSelectionDataLoading = useMemo(() => {
    if (!needsSelectionData || !appPart?.app_part_type) return false;
    const key = appPart.app_part_type as keyof AppSelectionList;
    return selectionDataList[key] === undefined;
  }, [needsSelectionData, appPart?.app_part_type, selectionDataList]);

  const options = useMemo(() => {
    if (!appPart) return [];

    if (isSelectionDataLoading) return [];

    const map = new Map<any, { label: string; value: any }>();
    data?.[columnId]
      ?.filter((x: any) => x !== "__NULL__")
      .forEach((x: any) => {
        if (!map.has(x)) {
          if (SELECTIONS_APP_PARTS.includes(appPart.app_part_type)) {
            const label = getTextSection(
              parseJson(x, []),
              appPart,
              selectionDataList,
            );

            if (label) {
              map.set(x, {
                label: getTextSection(
                  parseJson(x, []),
                  appPart,
                  selectionDataList,
                ),
                value: x,
              });
            }
          } else {
            const statusMap: Record<string, string> = {
              drafting: "新規作成中",
              submitted: "新規申請済（承認待）",
              reviewing: "申請受付済（確認中）",
              approved: "承認済（給与明細反映）",
              rejected: "却下",
            };
            const label =
              appType === APP_TYPES.BUSINESS_TRIP_REPORTS &&
              appPart.field_code === "status" &&
              statusMap[x]
                ? statusMap[x]
                : appPart.app_part_type === APP_PART_TYPES.SIGNED_DATE
                ? dayjs(x).format("YYYY年MM月DD日")
                : getValueFormatByAppPart(appPart, { value: x } as any);
            map.set(x, {
              label,
              value: getValueFilter(appPart, x, label),
            });
          }
        }
      });

    return Array.from(map.values());
  }, [
    data,
    appPart,
    columnId,
    isStatusContract,
    selectionDataList,
    isSelectionDataLoading,
    appType,
  ]);

  useEffect(() => {
    if (props.open) {
      refetch();
    }
  }, [props.open, refetch]);

  return (
    <div
      onClick={(e) => {
        e.stopPropagation();
      }}
    >
      <FilterPanel
        {...props}
        key={columnId}
        type={appPart?.app_part_type}
        options={options}
        ref={ref}
        isLoadingMoreOptions={isLoading || isSelectionDataLoading}
        isFetchingOptions={isFetching}
        hideSpecialEmptyCheckbox={
          appPart?.app_part_type === APP_PART_TYPES.STATUS_CONTRACT ||
          (data?.[columnId] ? !data[columnId].includes("__NULL__") : false)
        }
      />
    </div>
  );
};

export default forwardRef(FilterPanelWrapper);
